/**
 * Canonical Palworld XP-to-next-level preview line.
 *
 * Used by:
 *  • Pal Builder (`/builder`) — under the Level slider
 *  • Pal Box detail modal — under the in-game XP bar
 *
 * Reads from the datamined exp table at `/api/paldex/_meta/exp-table`.
 * The Builder loads the table once on mount and passes it in via the
 * `expTable` prop. For the Pal Box modal we lazily load it on first
 * render and memoise it on the window so subsequent opens are free.
 *
 * Behaviour:
 *  • Lv N (N < cap) → "Lv N → N+1: +X XP · Lv N total: Y"
 *  • Lv N (N >= cap) → "★ Lv N cap — fully evolved · Lv N total: Y"
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Cache the table on window so opening many Pal Box detail modals doesn't
// re-fetch the 26 KB JSON every time. The Builder still fetches it
// itself via its own Promise.all batch — we mirror the result here on
// first use so both surfaces share the same source of truth.
let _expTableCache = null;
let _expTablePromise = null;

function fetchExpTable() {
  if (_expTableCache) return Promise.resolve(_expTableCache);
  if (_expTablePromise) return _expTablePromise;
  _expTablePromise = api
    .get("/paldex/_meta/exp-table")
    .then((r) => {
      _expTableCache = r.data?.levels || {};
      return _expTableCache;
    })
    .catch(() => {
      _expTablePromise = null;  // allow retry next time
      return {};
    });
  return _expTablePromise;
}

export function PalXpPreview({ level, expTable: passed, maxLevel = 80, testid = "pal-xp-preview" }) {
  const [table, setTable] = useState(passed || _expTableCache);

  useEffect(() => {
    if (passed || _expTableCache) {
      setTable(passed || _expTableCache);
      return;
    }
    fetchExpTable().then((t) => setTable(t));
  }, [passed]);

  if (!table) return null;
  const cur = table[String(level)];
  if (!cur) return null;
  const next = table[String(level + 1)];

  const total = (cur.PalTotalEXP ?? 0).toLocaleString();
  const atCap = level >= maxLevel || !next;
  const toNext = atCap ? null : (next.PalNextEXP ?? 0).toLocaleString();

  return (
    <div
      className="mt-2 text-[10px] font-mono text-[var(--muted)] flex items-center justify-between gap-3"
      data-testid={testid}
    >
      {atCap ? (
        <span className="text-yellow-300/80 glow-gold">★ Lv {level} cap — fully evolved</span>
      ) : (
        <span>
          Lv {level} → {level + 1}:{" "}
          <span className="text-[var(--teal)] glow-teal">+{toNext}</span> XP
        </span>
      )}
      <span className="text-right">
        Lv {level} total:{" "}
        <span className="text-[var(--bone)]">{total}</span>
      </span>
    </div>
  );
}
